// Import necessary dependencies and components
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import axios from "axios";
import apiConfig from "./apiConfig";
import { useSelector } from "react-redux";
import "./style.scss";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function SetPassword() {
  // Initialize the navigation hook
  const navigate = useNavigate();

  // State to manage alert visibility and password visibility
  const [show, setShow] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Retrieve authentication token and temporary email from local storage
  let authToken = localStorage?.getItem("authToken");
  const tempemail = localStorage?.getItem("tempmail");

  // State to manage loading status
  const [loading, setloading] = useState(false);

  // Define validation schema using Yup
  const validationSchema = Yup.object({

    password: Yup.string()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~])[^\s]{8,}$/,
        "Password should contain at least one uppercase and lowercase character, one number, one special character, and must not contain spaces"
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  // Create a formik instance for form management
  const formik = useFormik({
    validateOnChange:false,
    validateOnBlur:false,
    initialValues: {
      email: tempemail,
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    // Handle form submission
    onSubmit: async (values) => {

      setloading(true);
      try {
        // Make an API request to set the user's password
        const response = await axios.post(
          `${apiConfig.baseURL}/set-password`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        setloading(false);
        toast.success("Password set successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        localStorage?.removeItem("tempmail");
        // Navigate to the login page
        navigate("/");
      } catch (error) {
        setloading(false);
        console.error("Password setting failed", error);
        toast.error("Password setting failed", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    },
  });

  // Framer Motion variants for animation
  const variants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0 },
  };

  // Function to toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Function to toggle confirm password visibility
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Return the JSX structure for the SetPassword component
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={variants}
      transition={{ duration: 0.5 }}
    >
      {/* Container for the entire SetPassword form */}
      <Container>
        {/* Row to center the form on the screen */}
        <Row
          className="justify-content-md-center"
          style={{ alignItems: "center", minHeight: "90vh" }}
        >
          {/* Column for the form, utilizing Bootstrap grid system */}
          <Col xs lg="6">
            {/* Card component for a styled form */}
            <Card className="border-0">
              {/* Form header */}
              <h2 className="fw-bolder mb-4 card-title text-center">
                Create New Password
              </h2>

              {/* Alert for password requirements */}
              <Alert
                variant="warning"
                className="mb-3"
                onClose={() => setShow(false)}
                dismissible
              >
                Use 8+ characters with at least 1 uppercase, 1 lowercase, 1
                number, and 1 special character
              </Alert>

              {/* Actual form using Formik */}
              <Form onSubmit={formik.handleSubmit} noValidate>


                {/* New Password input field */}
                <Form.Group
                  className="mb-4 position-relative"
                  controlId="formBasicPassword"
                >
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="New Password"
                    className="p-3"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      formik.touched.password && formik.errors.password
                    }
                  />
                  {/* Toggle icon for password visibility */}
                  <i
                    onClick={togglePasswordVisibility}
                    className={`password-icon ${
                      showPassword ? "visible" : "hidden"
                    }`}
                    style={{ position: "absolute", right: "31px", top: "15px" }}
                  >
                    {showPassword ? <BsEyeSlash /> : <BsEye />}
                  </i>
                  {/* Display password validation error if any */}
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.password}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Confirm Password input field */}
                <Form.Group
                  className="mb-3 position-relative"
                  controlId="formBasicConfirmPassword"
                >
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    className="p-3 "
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    }
                  />
                  {/* Toggle icon for confirm password visibility */}
                  <i
                    onClick={toggleConfirmPasswordVisibility}
                    className={`password-icon ${
                      showConfirmPassword ? "visible" : "hidden"
                    }`}
                    style={{ position: "absolute", right: "31px", top: "16px" }}
                  >
                    {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                  </i>
                  {/* Display confirm password validation error if any */}
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Set Password button */}
                <Button
                  type="submit"
                  variant="secondary"
                  size="lg"
                  className="rounded-pill text-white font-16 px-5 py-3 w-100"
                >
                  <Link
                    className="text-white text-decoration-none"
                    variant="primary"
                    type="submit"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        {/* Show loading spinner while setting the password */}
                      </>
                    ) : (
                      ""
                    )}
                    Set Password
                  </Link>
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
    </motion.div>
  );
}

// Export the SetPassword component
export default SetPassword;
